import { Component } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import * as saveAs from 'file-saver';
import { Observable, interval, map, startWith } from 'rxjs';
import { DatatableConfig } from 'src/app/config/DatatableConfig';
import { IListaAsignaturas, IParamsFiltrosQuery } from 'src/app/interfaces/ponderaciones';
import { IAnyos, IPeriodos } from 'src/app/interfaces/portal-calificaciones';
import { IResListadoReportes } from 'src/app/interfaces/reporte-consulta-estado-seccion';
import { NotasParcialesService } from 'src/app/services/notas-parciales/notas-parciales.service';
import { PerfilamientoService } from 'src/app/services/perfilamiento/perfilamiento.service';
import { PortalCalificacionesService } from 'src/app/services/portal-calificaciones/portal-calificaciones';
import { ReporteConsultaEstadoSeccionService } from 'src/app/services/reporte-consulta-estado-seccion/reporte-consulta-estado-seccion.service';
import { UtilsFnService } from 'src/app/services/utils/utils-fn.service';

@Component({
  selector: 'app-view-reporte-estado-avance-secciones',
  templateUrl: './view-reporte-estado-avance-secciones.component.html',
  styleUrls: ['./view-reporte-estado-avance-secciones.component.scss']
})
export class ViewReporteEstadoAvanceSeccionesComponent {
  dtOptions: DataTables.Settings = { ...DatatableConfig.dtSortWithoutSearch_2 }
  renderTable: boolean = false

  filtroAnyo: number = 0
  filtroPeriodo: number = 0
  filtroSede: number = 0
  filtroAsignaturas: number = 0
  filtroIdDocente: string = ''

  listaAnyo: Array<number> = []
  listaPeriodo: any = []
  listaSede: any = []
  listaAsignaturas: any = []
  listaNombreDocentes: any = []

  filtrosCargados: any = {}

  tieneErrores: any = {}
  submitted: boolean = false

  rolDaraCentral: boolean = false

  searchCodDocenteForm = new FormControl('')
  filteredCodDocentes: Observable<Array<any>>

  parametrosForm = this.formBuilder.group({
    anyo: ['0', [Validators.required]],
    periodo: ['0', [Validators.required]],
    sede: ['0'],
    asignatura: ['0'],
    idDocente: [''],
  })

  infoModalTitulo: string = ''
  infoModalTexto: string = ''

  listaReporteAvanceSecciones: any = []

  constructor(
    private _portalServices: PortalCalificacionesService,
    private _perfilamiento: PerfilamientoService,
    private router: Router,
    private formBuilder: FormBuilder,
    private _notasParciales: NotasParcialesService,
    private _reporteConsultaEstadoSeccion: ReporteConsultaEstadoSeccionService,
    private _utilsFn: UtilsFnService
  ) {
    let user = this._perfilamiento.getUsuario()
    if ( user.nombreRol === 'R_DARA_C' ) this.rolDaraCentral = true

    const routerData = this.router.getCurrentNavigation()?.extras?.state?.['data']
    if (routerData?.filtros) {
      this.filtrosCargados = routerData.filtros
    }

    this.filteredCodDocentes = this.searchCodDocenteForm.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '', 'cod')),
    )

    this.obtenerAnyos()
  }

  ngOnInit(): void {
    this.renderTable = true
    interval(60000).subscribe(() => {
      if ( this.filtroAnyo != 0 && this.filtroPeriodo != 0 && (this.filtroSede != 0 && this.rolDaraCentral ) ) {
        this.actualizarLista()
      }
    })
  }

  displayFnCodDocentes(docente: any): string {
    return docente?.codigo ?? ''
  }

  private _normalizeValue(value: string): string {
    return value?.toLowerCase()?.normalize("NFD")?.replace(/[\u0300-\u036f]/g, '')
  }

  private _filter(value: string, type: string): string[] {
    if (typeof value != 'string') {
      return []
    }
    const filterValue = this._normalizeValue(value)
    if ( type === 'cod' ) {
      return this.listaNombreDocentes.filter((docente: any) => this._normalizeValue(docente?.codigo)?.includes(filterValue) )
    } else {
      return this.listaNombreDocentes.filter((docente: any) => this._normalizeValue(`${docente?.primerNombre} ${docente?.apellidoPat} ${docente?.apellidoMat}`)?.includes(filterValue) )
    }
  }

  keypressNumber(event: any) {
    return this._utilsFn.keypressNumber(event)
  }

  resetearValidacion(tipo: string = 'default') {
    this.submitted = false
    this.tieneErrores["anyo"] = false
    this.tieneErrores["periodo"] = false
    this.tieneErrores["sede"] = false
    this.tieneErrores["idDocente"] = false
  }

  limpiarFiltros() {
    this.filtroAnyo = 0
    this.filtroPeriodo = 0
    this.listaPeriodo = []
    this.filtroSede = 0
    this.listaSede = []
    this.filtroAsignaturas = 0
    this.listaAsignaturas = []
    this.searchCodDocenteForm.patchValue('', {emitViewToModelChange: true, emitModelToViewChange: true, emitEvent: false})
    this.listaNombreDocentes = []
    this.filteredCodDocentes = this.searchCodDocenteForm.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '', 'cod')),
    )

    this.listaReporteAvanceSecciones = []
    this.renderTable = false
    setTimeout(() => {
      this.renderTable = true
    }, 500);
  }

  cargarFiltros(filtro: string): void {
    if ( this.filtroAnyo === 0 && this.filtroPeriodo === 0 ) {
      return
    }

    switch (filtro) {
      case "anyo":
        this.obtenerPeriodos(this.filtroAnyo)
        break;
      case "periodo":
        this.obtenerSedes(this.filtroAnyo, this.filtroPeriodo, 0)
        break;
      case "sede":
        this.obtenerAsignaturas(this.filtroAnyo, this.filtroPeriodo, this.filtroSede)
        this.obtenerDocentes(this.filtroAnyo, this.filtroPeriodo, this.filtroSede)
        break;
      case "asignatura":
        this.obtenerDocentes(this.filtroAnyo, this.filtroPeriodo, this.filtroSede, this.filtroAsignaturas)
        break;
      default:
        break;
    }
  }

  obtenerAnyos() {
    this._portalServices.obtenerListaAnhos().subscribe((res: IAnyos) => {
      this.listaAnyo = res.anyos
      if ( this.filtrosCargados?.anyo ) {
        this.filtroAnyo = this.filtrosCargados?.anyo
        delete this.filtrosCargados.anyo
      }
    })
  }

  obtenerDocentes(anyo: number, periodoId: number, sedeId: number = 0, asignaturaId: number = 0): void {
    this._notasParciales.getListaIdDocentes(anyo, periodoId, asignaturaId, sedeId).subscribe((res: any) => {
      this.listaNombreDocentes = res.docentes

      this.filteredCodDocentes = this.searchCodDocenteForm.valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value || '', 'cod')),
      )

      this.searchCodDocenteForm.patchValue(' ', {emitViewToModelChange: true, emitModelToViewChange: true, emitEvent: true})
      this.searchCodDocenteForm.patchValue('', {emitViewToModelChange: true, emitModelToViewChange: true, emitEvent: true})
    })
  }

  obtenerPeriodos(filtroAnyo: number): void {
    this.filtroPeriodo = 0
    this.filtroAsignaturas = 0
    this.filtroSede = 0
    this.listaSede = []
    this.listaAsignaturas = []

    if ( filtroAnyo !== 0 ) {
      this.tieneErrores["anyo"] = false
      this._portalServices.obtenerListaPeriodos(filtroAnyo).subscribe(
        (res: IPeriodos) => {
          this.listaPeriodo = res.periodos
        }
      )
    }
  }

  obtenerSedes(anyo: number = 0, periodoId: number = 0, funcionarioId: number = 0): void {
    this.filtroAsignaturas = 0
    this.filtroSede = 0
    this.listaSede = []
    this.listaAsignaturas = []
    this._portalServices.obtenerListaSedesBackoffice(anyo, periodoId, funcionarioId).subscribe((res: any) => {
      this.listaSede = res
    })
  }

  obtenerAsignaturas(anyo: number = 0, periodoId: number = 0, sedeId: number = 0): void {
    const filter: IParamsFiltrosQuery = { anyo, periodoId, sedeId }
    this._notasParciales.getFiltrosAsignaturas(filter).subscribe(
      (res: IListaAsignaturas) => {
        this.listaAsignaturas = res.asignaturas
      }
    )
  }

  actualizarLista(): void {
    this.submitted = true
    const dataDocente: any = this.searchCodDocenteForm.getRawValue()
    const idDocente = dataDocente?.docenteId ? dataDocente.docenteId : 0
    if ( this.filtroAnyo === 0 ) this.tieneErrores["anyo"] = true
    if ( this.filtroPeriodo === 0 ) this.tieneErrores["periodo"] = true
    if ( this.filtroSede === 0 ) this.tieneErrores["sede"] = true // && !this.rolDaraCentral
    const filtrosConErrores = Object.values(this.tieneErrores).filter(value => value === true)
    if ( filtrosConErrores.length > 0 ) {
      return
    }
    this.renderTable = false
    this._reporteConsultaEstadoSeccion.getListadoReportes(this.filtroAnyo, this.filtroPeriodo, this.filtroSede, this.filtroAsignaturas, idDocente).subscribe(
      (res: IResListadoReportes) => {
        this.listaReporteAvanceSecciones = res.solicitudes
        this.renderTable = true
      }, err => {
        this.listaReporteAvanceSecciones = []
        this.renderTable = true
      }
    )
  }

  generarReporte() {
    this.submitted = true
    if ( this.filtroAnyo === 0 ) this.tieneErrores["anyo"] = true
    if ( this.filtroPeriodo === 0 ) this.tieneErrores["periodo"] = true
    if ( this.filtroSede === 0 ) this.tieneErrores["sede"] = true // && !this.rolDaraCentral
    const filtrosConErrores = Object.values(this.tieneErrores).filter(value => value === true)
    if ( filtrosConErrores.length > 0 ) {
      return
    }
    const dataDocente: any = this.searchCodDocenteForm.getRawValue()
    const idDocente = dataDocente?.docenteId ? dataDocente.docenteId : 0
    this._reporteConsultaEstadoSeccion.postGeneraSolicitudReporte(this.filtroAnyo, this.filtroPeriodo, this.filtroSede, this.filtroAsignaturas, idDocente).subscribe(
      (res: IResListadoReportes) => {
        if ( res.valor === true ) {
          this.mostrarAlertaExito('El reporte ha sido generado exitosamente.')
          this.actualizarLista()
        } else {
          this.mostrarInfoModal('Error', res?.error?.mensaje ? res.error.mensaje : 'Hubo un error en los servicios.')
        }
      }
    )
  }

  obtenerReporteExcel(idReporte: number): void {
    this._reporteConsultaEstadoSeccion.getDescargaReporte(idReporte).subscribe(
        async (res: any) => {
        const contentDispositionHeader = res?.headers?.get('Content-Disposition')
        const fileName = contentDispositionHeader?.split(';')[1]?.trim()?.split('=')[1]
        const bufferPromise = await res?.body.arrayBuffer();
        const csvData = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), bufferPromise], {type: "text/plain;charset=utf-8"});
        saveAs(csvData, fileName?.replace(/"/g, '') ?? 'reporteConsultaEstadoSeccion.csv')
      }
    )
  }

  navToCrearReemplazoDocente(): void {
    this.router.navigate(['/reemplazo-docente-crear'])
  }

  camposRequeridos(): boolean {
    const dataDocente: any = this.searchCodDocenteForm.getRawValue()
    return this.filtroAnyo !== 0 && this.filtroPeriodo !== 0 && (typeof dataDocente === 'object')
  }

  dataEliminar: any = null
  alertaExitosa: string = ''

  mostrarAlertaExito(texto: string) {
    this.alertaExitosa = texto
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setTimeout(() => {
      this.alertaExitosa = ''
    }, 3000);
  }

  mostrarInfoModal(titulo: string, texto: string) {
    this.infoModalTitulo = titulo
    this.infoModalTexto = texto
    document.getElementById('btn-modal-info')?.click()
  }
}
